import React, { useState, useEffect, useContext } from "react"
import { notGatsby, graphql } from "gatsby"
import PropTypes from "prop-types"
import propTypes from "@styled-system/prop-types"
import styled, { ThemeContext } from "styled-components"

import ThemeSwitcher from "../utils/ThemeSwitcher"

import VerticalText from "./VerticalText"
import Box from "../atoms/Box"
import Flex from "../atoms/Flex"
import Button from "../atoms/Button"
import StandardPadding from "../atoms/StandardPadding"
import Parallax from "../atoms/MorphicParallax/MorphicParallax"
import ImageOrVideo from "./ImageOrVideo"

const StyledImageOrVideo = styled(ImageOrVideo)`
  width: ${props => (props.isVideoSelected ? "100vw" : "100%")};
  height: ${props => (props.isVideoSelected ? "100vh" : "100%")};
  iframe {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100vw;
    height: 100vh;
    transform: translate(-50%, -50%);
  }
  video {
    position: absolute;
    top: 0;
    left: 0;
    object-fit: cover;
    width: 100vw;
    height: 100vh;
  }

  img {
    /* styles for video fallback image. This applies when a video is play for mobile but the particular 
    mobile devices failed to play the video due to particular device limition or settings.
    // Using the IMPORTANT keyword is needed here to overwrite styles coming from different video providers
    */

    ${props => (props.isVideoSelected ? "position:absolute !important" : null)};
    ${props => (props.isVideoSelected ? "top:50% !important" : null)};
    ${props => (props.isVideoSelected ? "left:50% !important" : null)};
    ${props => (props.isVideoSelected ? "object-fit:cover !important" : null)};
    ${props =>
      props.isVideoSelected
        ? "transform:translate(-50%, -50%) !important"
        : null};
    ${props => (props.isVideoSelected ? "height:100vh !important" : null)};
    ${props => (props.isVideoSelected ? "width:100vw !important" : null)};
  }

  & img.video-fallback-image {
    height: 100vh;
  }
  & img {
    &.mobile-image,
    &.desktop-image {
      height: 100%;
      height: 100%;
    }
  }

  @media (min-aspect-ratio: 1/2) {
    iframe {
      /* height = 100 * (2 / 1) = 200 */
      height: 200vw !important;
    }
  }
  @media (max-aspect-ratio: 1/2) {
    iframe {
      /* width = 100 / (1 / 2) = 50 */
      width: 50vh !important;
    }
  }
  @media (min-aspect-ratio: 4/3) {
    iframe {
      /* height = 100 * (3 / 4) = 75 */
      height: 75vw !important;
    }
  }
  @media (max-aspect-ratio: 4/3) {
    iframe {
      /* width = 100 / (4 / 3) = 133.33 */
      width: 133.33vh !important;
    }
  }
  @media (min-aspect-ratio: 16/9) {
    iframe,
    video {
      //defaulting video with video tag to 16/9 ratio
      /* height = 100 * (9 / 16) = 56.25 */
      height: 56.25vw !important;
    }
  }
  @media (max-aspect-ratio: 16/9) {
    iframe,
    video {
      //defaulting video with video tag to 16/9 ratio
      /* width = 100 / (16 / 9) = 177.777777 */
      width: 177.78vh !important;
    }
  }
  @media (min-aspect-ratio: 1/1.77) {
    iframe {
      /* height = 100 * (1.77 / 1) = 177 */
      height: 177vw !important;
    }
  }
  @media (max-aspect-ratio: 1/1.77) {
    iframe {
      /* width = 100 / (1 / 1.77 ) = 56.497 */
      width: 56.5vh !important;
    }
  }
  @media (min-aspect-ratio: 1/1.9) {
    iframe {
      /* height = 100 * (1.9 / 1) = 190 */
      height: 190vw !important;
    }
  }
  @media (max-aspect-ratio: 1/1.9) {
    iframe {
      /* width = 100 / (1 / 1.9) = 52.63 */
      width: 52.63vh !important;
    }
  }
`

const DividingLine = ({ height }) => {
  return (
    <Box
      position="relative"
      width="1px"
      height={height}
      bg="text"
      style={{ opacity: 0.3 }}
    />
  )
}

const StyledBox = styled(Box)`
  ${props =>
    props.sectionBreak === "slope"
      ? `clip-path: polygon(100% 100%, 100% 0%, 0% 0%, 0% calc(100% - 80px))` //64px
      : null};
  @media only screen and (max-width: ${props =>
      props.sectionBreak === "slope" && props.theme.breakpoints.md}) {
    clip-path: polygon(100% 100%, 100% 0%, 0% 0%, 0% calc(100% - 48px)); //32px
  }
  & div {
    ${props =>
      props.maxWidth ? `max-width: ${props.maxWidth} !important` : null}
  }
`

const Section = ({
  id,
  sectionId,
  children,
  theme,
  buttons,
  isVideoSelected,
  backgroundImageMobile,
  backgroundImageDesktop,
  videoImageFallback,
  videoURL,
  overlayOpacity,
  parallaxOptions,
  parallaxWeight,
  bg,
  sectionDivider,
  sectionBreak,
  prevSection,
  zIndex,
  verticalPadding,
  customVerticalPadding,
  noStandardPadding,
  maxWidth,
  display,
  contentLocation,
  fullWidthOptionSelected,
  verticalText,
  isFullHeight,
  ...rest
}) => {
  isVideoSelected = !isVideoSelected ? false : isVideoSelected
  contentLocation = !contentLocation ? "start" : contentLocation
  noStandardPadding =
    noStandardPadding &&
    (verticalPadding !== "custom" ||
      (verticalPadding === "custom" && customVerticalPadding < 0))
  verticalPadding = !verticalPadding ? "md" : verticalPadding
  customVerticalPadding = !customVerticalPadding ? 0 : customVerticalPadding
  maxWidth = !maxWidth
    ? "1120px"
    : typeof maxWidth === "number" && maxWidth === 2000
    ? "100%"
    : typeof maxWidth === "number"
    ? `${maxWidth}px`
    : maxWidth

  const background = `background.${bg}`
  const sectionTheme = useContext(ThemeContext)
  const getParallaxHex = (bg, theme, sectionTheme) => {
    if (theme === "default") theme = sectionTheme?.backgroundTheme
    let hexCode = sectionTheme?.themes?.[theme]?.colors?.background?.[bg]
    if (!hexCode) hexCode = "#FFFFFF"
    return hexCode
  }

  const { breakpoints } = useContext(ThemeContext)

  const [parallaxParent, setParallaxParent] = useState(null)
  useEffect(() => {
    const getParentElement = () => {
      if (!notGatsby) {
        setParallaxParent(document)
      } else {
        setParallaxParent(document.querySelector("#site-preview"))
      }
    }

    getParentElement()
  }, [parallaxOptions])

  let media = (
    <StyledImageOrVideo
      desktopImage={backgroundImageDesktop}
      mobileImage={backgroundImageMobile}
      videoImageFallback={videoImageFallback}
      videoURL={videoURL}
      isVideoSelected={isVideoSelected}
      playing={true}
      loop={true}
      controls={false}
      volume={0}
      muted={true}
      playsinline={true}
      videoProps={{
        width: "100vw",
        height: "100vh",
      }}
      width={isVideoSelected ? "100vw" : "100%"}
      height={isVideoSelected ? "100vh" : "100%"}
    />
  )

  let dividerHeight
  switch (verticalPadding) {
    case "lg":
      dividerHeight = { _: 4, md: 5 }
      break
    case "default":
    case "md":
      dividerHeight = { _: 2, md: 3 }
      break
    case "sm":
      dividerHeight = { _: 1, md: 2 }
      break
    case "custom":
      const defaultPadding =
        Math.floor(customVerticalPadding / 32) >= 2
          ? customVerticalPadding - 16
          : 32
      dividerHeight = {
        _: `${defaultPadding}px`,
        md: `${customVerticalPadding}px`,
      }
      //overwritting sectionBreak default padding
      rest.pt = "0px"
      rest.pb = "0px"
      break
    default:
      dividerHeight = { _: 2, md: 3 }
  }

  const standardPaddingProps = {
    verticalPadding: verticalPadding,
    py: dividerHeight,
    position: "relative",
    zIndex: 10,
    maxWidth: maxWidth,
    margin: "0px auto",
    width: "100%",
    px:
      (verticalPadding === "custom" && maxWidth === "100%") || isVideoSelected
        ? 0
        : { _: 5, lg: 7, xl: 4 },
  }

  let ctaButtons =
    buttons && buttons.length
      ? buttons.map(button => (
          <Button aria-label="CTA button" {...button} mx={4} my={3} />
        ))
      : null

  const innerContent = (
    <Box
      zIndex={15}
      maxWidth={maxWidth}
      margin="0px auto"
      display={display}
      width="100%"
      {...rest.contentStyles}
    >
      {children}
      {ctaButtons && (
        <Flex width="100%" justifyContent="center" flexWrap="wrap" mt={6}>
          {ctaButtons}
        </Flex>
      )}
    </Box>
  )
  const parallaxOn =
    parallaxOptions === null
      ? false
      : parallaxOptions === undefined
      ? false
      : parallaxOptions === "noParallax"
      ? false
      : parallaxOptions === "fixed" && isVideoSelected
      ? false
      : true

  return (
    <ThemeSwitcher theme={theme} height="100%">
      <StyledBox
        as="section"
        id={sectionId || id}
        position={"relative"}
        bg={media ? null : background}
        zIndex={zIndex}
        sectionBreak={sectionBreak}
        display="flex"
        flexDirection={!notGatsby && parallaxOn ? "column" : "row"}
        alignItems={!notGatsby && parallaxOn ? null : contentLocation}
        // pb={sectionBreak === "slope" ? { _: 7, md: 10, lg: 10 } : null}
        {...rest}
      >
        {media && parallaxOn && !notGatsby && (
          <Parallax
            notGatsby={notGatsby}
            noStandardPadding={noStandardPadding}
            parallaxOptions={parallaxOptions}
            imageOrVideo={media}
            strength={parallaxWeight}
            bgImageStyle={{ minWidth: "100%" }}
            parallaxOpacity={overlayOpacity ? overlayOpacity : 0}
            parallaxHex={getParallaxHex(bg, theme, sectionTheme)}
            parent={parallaxParent}
            standardPaddingProps={standardPaddingProps}
            isFullHeight={isFullHeight}
            contentLocation={contentLocation}
          >
            {innerContent}
          </Parallax>
        )}
        {verticalText && (
          <VerticalText
            {...verticalText}
            pb={rest.pt ? rest.pt : 0} // Adjusting for section padding to prevent vertical text overflow
            pt={rest.pb ? rest.pb : 0} // Adjusting for section padding to prevent vertical text overflow
            mb={rest.mt ? rest.mt : 0} // Adjusting for section margin to prevent vertical text overflow
            mt={rest.mb ? rest.mb : 0} // Adjusting for section margin to prevent vertical text overflow
          />
        )}
        {((media && !parallaxOn) || (media && parallaxOn && notGatsby)) && (
          <>
            <Box
              position="absolute"
              zIndex={-1}
              top="0"
              right="0"
              bottom="0"
              left="0"
              bg={background}
              overflow="hidden"
            >
              {media}
            </Box>
            <Box
              position="absolute"
              zIndex={1}
              top="0"
              right="0"
              bottom="0"
              left="0"
              bg={background}
              opacity={overlayOpacity || 0}
            />
          </>
        )}
        {((!noStandardPadding && !parallaxOn) || //has padding, no parallax
          (!noStandardPadding && parallaxOn && notGatsby)) && ( //has padding, has parallax, morphic
          <StandardPadding {...standardPaddingProps}>
            {innerContent}
          </StandardPadding>
        )}
        {((noStandardPadding && !parallaxOn) || //no padding, no parallax
          (noStandardPadding && parallaxOn && notGatsby)) && //no padding, has parallax, morphic
          innerContent}
        {sectionDivider === "line" && (
          <Flex
            bottom={0}
            position="absolute"
            width="100%"
            justifyContent="center"
          >
            <DividingLine height={dividerHeight} />
          </Flex>
        )}
      </StyledBox>
    </ThemeSwitcher>
  )
}
Section.defaultProps = {
  theme: "default",
}
Section.strapiProps = {
  theme: PropTypes.oneOf(["default", "light", "dark", "highlight"]).isRequired,
  bg: PropTypes.number.isRequired,
  backgroundImageMobile: PropTypes.object,
  backgroundImageDesktop: PropTypes.object,
  overlayOpacity: PropTypes.number,
  sectionDivider: PropTypes.oneOf(["default", "line"]),
}
Section.propTypes = {
  children: PropTypes.node.isRequired,
  ...propTypes.color,
  ...Section.strapiProps,
}
export default Section
export const query = graphql`
  fragment Section on STRAPI__COMPONENT_ATOMS_SECTION {
    strapi_id
    bg
    theme
    buttons {
      ...Button
    }
    isVideoSelected
    backgroundImageMobile {
      ...ImageWithFullWidthLayout
    }
    backgroundImageDesktop {
      ...ImageWithFullWidthLayout
    }
    backgroundImageDesktop {
      ...ImageWithFullWidthLayout
    }
    videoImageFallback {
      ...ImageWithFullWidthLayout
    }
    videoURL
    overlayOpacity
    parallaxOptions
    parallaxWeight
    hidden
    sectionDivider
    contentLocation
    verticalPadding
    sectionBreak
    customVerticalPadding
    maxWidth
  }
`
